import { createSlice } from "@reduxjs/toolkit";
import {
  COLUMN_MAPPING_STATUS,
  COLUMN_MAPPING_STATUS_ACTION,
} from "../../constants/columnMappingStatus";
import { createAjv } from "@jsonforms/core";
import { isNull, isUndefined } from "lodash";

//#region Helpers functions
const validator = createAjv();

//#region ColumnMapping
const getColumnMapping = (columnNames, column) => {
  let bufferCol = columnNames.find(
    (x) => column.name.toLowerCase() === x.toLowerCase()
  );
  if (bufferCol) {
    return {
      src: bufferCol,
      dest: column.name,
      required: column.required,
      status: COLUMN_MAPPING_STATUS.MATCHED,
      action: COLUMN_MAPPING_STATUS_ACTION.EDIT,
    };
  }

  bufferCol = columnNames.find(
    (x) =>
      x.toLowerCase().includes(column.name.toLowerCase()) ||
      column.name.toLowerCase().includes(x.toLowerCase())
  );
  if (bufferCol) {
    return {
      src: bufferCol,
      dest: column.name,
      required: column.required,
      status: COLUMN_MAPPING_STATUS.POSSIBLY_MATCHED,
      action: COLUMN_MAPPING_STATUS_ACTION.EDIT,
    };
  }

  return {
    src: null,
    dest: column.name,
    required: column.required,
    status: COLUMN_MAPPING_STATUS.NO_MATCH,
    action: COLUMN_MAPPING_STATUS_ACTION.EDIT,
  };
};

const getColumnMappings = (expectedFields, columnNames) => {
  let colMappings = [];

  for (let field of expectedFields) {
    colMappings.push(getColumnMapping(columnNames, field));
  }

  return colMappings;
};
//#endregion

//#region ValidationStatistics
const getPercentageOfEmptyRows = (colName, fileData) => {
  let values = fileData.map((x) => x[colName]);

  if (!values.length) return 0;
  let nonEmptyValuesCount = values.filter(
    (x) => isUndefined(x) || isNull(x) || x.toString().trim("").length === 0
  ).length;

  return (nonEmptyValuesCount / values.length) * 100;
};

const getValidationStatistics = (columnNames, fileData) => {
  let validationStatistics = {};

  if (fileData.length > 0) {
    columnNames.map((prop) => {
      validationStatistics[prop] = {
        percentage: getPercentageOfEmptyRows(prop, fileData),
        errors: 0,
      };
    });
  }
  return validationStatistics;
};
//#endregion

//#region ValidateFile
const getErrorProperty = (error) => {
  if (error.keyword === "required") return error.params.missingProperty;
  else if (
    error.keyword === "type" ||
    error.keyword === "minLength" ||
    error.keyword === "maxLength" ||
    error.keyword === "enum" ||
    error.keyword === "format" ||
    error.keyword === "pattern"
  ) {
    return error.instancePath.substring(1);
  } else {
    //TODO: add other error types
  }
};

const FormatFileFields = (row, schema) => {
  Object.keys(row).forEach((key) => {
    if (schema.properties[key]) {
      
      // row[key] is the value of the cell
      // schema.properties[key].type is the type of the cell
      // schema.required is an array of the required fields

      const isEmpty = row[key] === undefined || row[key].trim() === "";
      const isRequired = schema.required && schema.required.includes(key);

      const hasFormatDate = schema.properties[key].format === "date";
      const isTypeBoolean = schema.properties[key].type?.includes("boolean");
      const isTypeInteger = schema.properties[key].type?.includes("integer");
      const isTypeNumber = schema.properties[key].type?.includes("number");
      const isTypeString = schema.properties[key].type?.includes("string");

      if (isEmpty) {
        if ((isRequired && isTypeString) || ((!isRequired && (hasFormatDate || isTypeBoolean || isTypeInteger || isTypeNumber)))) {
          row[key] = null;
        } 
      }
      else {
        if (isTypeBoolean) {
          // parse cell value as boolean
          row[key] = row[key].toLowerCase() === "true";
        } 
        else if (isTypeInteger || isTypeNumber) {
          // parse cell value as number
          row[key] = Number(row[key]);
        }
      }
    }
  });
};

const getErrorsXLSX = (columnMappings, schema, data, t) => {
  let fileData = data.map((row) => {
    let result = {};
    for (let i = 0; i < columnMappings.length; i++) {
      let mappedKey = columnMappings[i].dest;
      if (
        mappedKey &&
        columnMappings[i].action !== COLUMN_MAPPING_STATUS_ACTION.IGNORED
      ) {
        result[mappedKey] = row[columnMappings[i].src];
      }
    }
    return result;
  });

  const validate = validator.compile(schema);
  let result = [];

  fileData.map((row, index) => {
    FormatFileFields(row, schema);
    const valid = validate(row);
    if (valid) {
      return;
    }
    for (let error of validate.errors) {
      let errorField = error.instancePath.substring(1);
      let message;
      switch (error.keyword) {
      case "required":
        errorField = error.params.missingProperty;
        message = t(
          "fileUploadWizard.uploadFileResults.errorsFile.errorMessages.required"
        );
        break;
      case "type":
        message = t(
          "fileUploadWizard.uploadFileResults.errorsFile.errorMessages.type",
          {
            value: error.params.type,
          }
        );
        break;
      case "enum":
        message = t(
          "fileUploadWizard.uploadFileResults.errorsFile.errorMessages.enum",
          {
            value: error.params.allowedValues.toString(),
          }
        );
        break;
      case "minLength":
        if (error.params.limit === 1) {
          message = t(
            "fileUploadWizard.uploadFileResults.errorsFile.errorMessages.required"
          );
          break;
        }
        message = t(
          "fileUploadWizard.uploadFileResults.errorsFile.errorMessages.minLength",
          {
            value: error.params.limit,
          }
        );
        break;
      case "maxLength":
        message = t(
          "fileUploadWizard.uploadFileResults.errorsFile.errorMessages.maxLength",
          {
            value: error.params.limit,
          }
        );
        break;
      case "format":
        message = t(
          "fileUploadWizard.uploadFileResults.errorsFile.errorMessages.format",
          {
            value: error.params.format,
          }
        );
        break;
      case "pattern":
        message = t(
          "fileUploadWizard.uploadFileResults.errorsFile.errorMessages.pattern"
        );
        break;
      default:
        message = t(
          "fileUploadWizard.uploadFileResults.errorsFile.errorMessages.default"
        );
        break;
      }
      result.push({
        row: index + 2, //To match excel row
        column: errorField,
        value: row[errorField],
        error: message,
      });
    }
  });

  return result;
};

const getFileValidationResults = (
  columnMappings,
  schema,
  validationStatistics,
  data
) => {
  let fileData = data.map((row) => {
    let result = {};
    for (let i = 0; i < columnMappings.length; i++) {
      let mappedKey = columnMappings[i].dest;
      if (
        mappedKey &&
        columnMappings[i].action !== COLUMN_MAPPING_STATUS_ACTION.IGNORED
      ) {
        result[mappedKey] = row[columnMappings[i].src];
      }
    }
    return result;
  });

  const validate = validator.compile(schema);

  let errors = [];
  let invalidRows = 0;
  let propertyErrors = {};

  fileData.forEach((row) => {
    FormatFileFields(row, schema);
    let valid = validate(row);
    if (!valid) {
      invalidRows++;
      let invalidProperties = [];
      validate.errors.forEach((x) => {
        let property = getErrorProperty(x);
        if (!property || invalidProperties.includes(property)) return;

        invalidProperties.push(property);

        if (propertyErrors[property]) {
          propertyErrors[property]++;
        } else {
          propertyErrors[property] = 1;
        }
      });
      errors = [...errors, ...validate.errors];
    }
  });

  Object.keys(validationStatistics).forEach((x) => {
    let colMapping = columnMappings.find((y) => y.src === x)?.dest;
    validationStatistics[x].errors =
      colMapping === null || !propertyErrors[colMapping]
        ? 0
        : (propertyErrors[colMapping] / data.length) * 100;
  });

  return {
    errors,
    invalidRows,
    validRows: fileData.length - invalidRows,
    validationStatistics,
  };
};
//#endregion

//#endregion

const initialData = {
  schema: null,
  validationStatistics: null,
  expectedFields: null,
  srcColNames: [],
  validRows: 0,
  invalidRows: 0,
  columnMappings: null,
  errors: null,
  errorsXLSX: null,
  file: { name: null, size: null },
};

const fileUploadSlice = createSlice({
  name: "fileUpload",
  initialState: initialData,
  reducers: {
    setInitialData(state, action) {
      let expectedFields = Object.keys(action.payload.schema.properties).map(
        (prop) => ({
          name: prop,
          required: action.payload.schema.required.includes(prop),
        })
      );
      let columnMappings = getColumnMappings(
        expectedFields,
        action.payload.columnNames
      );
      let validationStatistics = getValidationStatistics(
        action.payload.columnNames,
        action.payload.fileData
      );
      let validationRes = getFileValidationResults(
        columnMappings,
        action.payload.schema,
        validationStatistics,
        action.payload.fileData
      );
      let errorsXLSX = getErrorsXLSX(
        columnMappings,
        action.payload.schema,
        action.payload.fileData,
        action.payload.t
      );
      state.file.name = action.payload.fileName;
      state.file.size = action.payload.fileSize;
      state.schema = action.payload.schema;
      state.srcColNames = action.payload.columnNames;
      state.expectedFields = expectedFields;
      state.columnMappings = columnMappings;
      state.validationStatistics = validationRes.validationStatistics;
      state.validRows = validationRes.validRows;
      state.invalidRows = validationRes.invalidRows;
      state.errors = validationRes.errors;
      state.errorsXLSX = errorsXLSX;
    },
    resetData(state) {
      state.file.name = initialData.file.name;
      state.file.size = initialData.file.size;
      state.schema = initialData.schema;
      state.srcColNames = initialData.columnNames;
      state.expectedFields = initialData.expectedFields;
      state.columnMappings = initialData.columnMappings;
      state.validationStatistics = initialData.validationStatistics;
      state.validRows = initialData.validRows;
      state.invalidRows = initialData.invalidRows;
      state.errors = initialData.errors;
      state.errorsXLSX = initialData.errorsXLSX;
    },
    setColumnMappings(state, action) {
      const mapping = state.columnMappings.find(
        (x) => x.dest === action.payload.dest
      );
      mapping.src = action.payload.src;
      mapping.status = COLUMN_MAPPING_STATUS.MANUAL_MATCH;

      let validationRes = getFileValidationResults(
        JSON.parse(JSON.stringify(state.columnMappings)),
        JSON.parse(JSON.stringify(state.schema)),
        JSON.parse(JSON.stringify(state.validationStatistics)),
        action.payload.fileData
      );
      let errorsXLSX = getErrorsXLSX(
        state.columnMappings,
        state.schema,
        action.payload.fileData,
        action.payload.t
      );
      state.validRows = validationRes.validRows;
      state.invalidRows = validationRes.invalidRows;
      state.errors = validationRes.errors;
      state.validationStatistics = validationRes.validationStatistics;
      state.errorsXLSX = errorsXLSX;
    },
    setColumnAction(state, action) {
      const colMapping = state.columnMappings.find(
        (x) => x.dest === action.payload.dest
      );
      colMapping.action = action.payload.action;
      let validationRes = getFileValidationResults(
        JSON.parse(JSON.stringify(state.columnMappings)),
        JSON.parse(JSON.stringify(state.schema)),
        JSON.parse(JSON.stringify(state.validationStatistics)),
        action.payload.fileData
      );
      state.validRows = validationRes.validRows;
      state.invalidRows = validationRes.invalidRows;
      state.errors = validationRes.errors;
      state.validationStatistics = validationRes.validationStatistics;
    },
  },
});

export const fileUploadActions = fileUploadSlice.actions;
export default fileUploadSlice.reducer;
